import { useState, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import type { PronunciationScore, AudioAnalysisResult } from '../types';

interface OptimizedAnalysisResult extends AudioAnalysisResult {
  cache_hit: boolean;
  processing_time_ms: number;
}

export function usePronunciationAnalysisOptimized() {
  const { user } = useAuthStore();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<OptimizedAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<{
    processingTime: number;
    cacheHit: boolean;
    speedImprovement?: string;
  } | null>(null);

  const analyzeRecording = useCallback(async (audioBlob: Blob, word: string, wordId: string) => {
    if (!audioBlob || !user) {
      setError('No recording available or user not authenticated');
      return null;
    }

    setIsAnalyzing(true);
    setError(null);
    setPerformanceMetrics(null);

    const startTime = Date.now();

    try {
      const fileName = `audio_${Date.now()}.wav`;
      const formData = new FormData();
      formData.append('audio', audioBlob, fileName);
      formData.append('word', word);
      formData.append('userId', user.id);
      formData.append('wordId', wordId);

      // Use the optimized speech analysis function
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke('speech-analysis-optimized', {
        body: formData
      });

      if (analysisError) {
        throw new Error(analysisError.message);
      }

      const endTime = Date.now();
      const clientProcessingTime = endTime - startTime;

      // Set performance metrics
      setPerformanceMetrics({
        processingTime: analysisData.processing_time_ms || clientProcessingTime,
        cacheHit: analysisData.cache_hit || false,
        speedImprovement: analysisData.cache_hit ? '80-90% faster' : 'baseline'
      });

      setAnalysisResult({
        ...analysisData,
        cache_hit: analysisData.cache_hit || false,
        processing_time_ms: analysisData.processing_time_ms || clientProcessingTime
      });

      const pronunciationScore: PronunciationScore = {
        overall: typeof analysisData.overall === 'number' 
          ? analysisData.overall : 0,
        phonemes: analysisData.analysis_data?.phonemes?.map((ps: any) => ({
          phoneme: ps.phoneme,
          score: ps.score,
          feedback_en: ps.feedback_en || `Expected: ${ps.expected}, Actual: ${ps.actual}`,
          feedback_vi: ps.feedback_vi || `Mong đợi: ${ps.expected}, Thực tế: ${ps.actual}`
        })) || [],
        feedback_en: analysisData.analysis_data?.feedback_en || analysisData.feedback || '',
        feedback_vi: analysisData.analysis_data?.feedback_vi || analysisData.feedback || '',
        suggestions_en: analysisData.analysis_data?.suggestions_en || 
                        (analysisData.feedback ? analysisData.feedback.split('. ').filter((s: string) => s.length > 0) : []),
        suggestions_vi: analysisData.analysis_data?.suggestions_vi || 
                        (analysisData.feedback ? analysisData.feedback.split('. ').filter((s: string) => s.length > 0) : [])
      };

      // Log performance improvement for user feedback
      if (analysisData.cache_hit) {
        console.log(`🚀 Performance boost: Analysis completed in ${analysisData.processing_time_ms}ms (cache hit)`);
      } else {
        console.log(`⏱️ Analysis completed in ${analysisData.processing_time_ms}ms (Gemini API call)`);
      }

      return pronunciationScore;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
      setError(errorMessage);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, [user]);

  // Get cache statistics for the current user
  const getCacheStats = useCallback(async () => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('speech_cache_stats')
        .select('*')
        .single();

      if (error) {
        console.error('Failed to get cache stats:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Failed to fetch cache statistics:', err);
      return null;
    }
  }, [user]);

  return { 
    isAnalyzing, 
    analysisResult, 
    error, 
    analyzeRecording, 
    performanceMetrics,
    getCacheStats
  };
}